import { Suspense, lazy, useEffect, useState } from "react";
import "./index.css";
import yanshLogo from "./assets/yansh-logo.png";
import CRMSidebar from "./components/dashboard/CRMSidebar";
import TopBar from "./components/dashboard/TopBar";
import SEO from "./components/SEO";
import ManagedSettingsGate from "./components/ManagedSettingsGate";
import { adminPath, isAdminEntryPath, stripAdminBase } from "./config/adminRouting";
import { withQuery } from "./utils/urlFilters";
import { watchCardTables } from "./utils/tableCardLabels";
import { ROLES } from "./config/roles";
import { useAuth } from "./context/AuthContext";
import ProtectedRoute, { canOpenPage } from "./routes/ProtectedRoute";

const DashboardModern = lazy(() => import("./pages/DashboardModern"));
const LeadsPage = lazy(() => import("./pages/Dashboard"));
const AddService = lazy(() => import("./pages/AddService"));
const RepairsModern = lazy(() => import("./pages/RepairsModern"));
const Users = lazy(() => import("./pages/Users"));
const Products = lazy(() => import("./pages/Products"));
const Inventory = lazy(() => import("./pages/Inventory"));
const HeroSlides = lazy(() => import("./pages/HeroSlides"));
const Popups = lazy(() => import("./pages/Popups"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Billing = lazy(() => import("./pages/Billing"));
const Estimates = lazy(() => import("./pages/Estimates"));
const Payments = lazy(() => import("./pages/Payments"));
const Purchases = lazy(() => import("./pages/Purchases"));
const ProfitLoss = lazy(() => import("./pages/ProfitLoss"));
const Orders = lazy(() => import("./pages/Orders"));
const WhatsApp = lazy(() => import("./pages/WhatsApp"));
const CustomerProfile = lazy(() => import("./pages/CustomerProfile"));
const WhatsAppTemplateManagement = lazy(() => import("./pages/WhatsAppTemplateManagement"));
const EmailSettings = lazy(() => import("./pages/EmailSettings"));
const DeviceMasters = lazy(() => import("./pages/DeviceMasters"));
const PaymentAccountSettings = lazy(() => import("./pages/billing/components/PaymentAccountSettings"));
const AnalyticsDashboard = lazy(() => import("./pages/billing/components/AnalyticsDashboard"));
const Warranty = lazy(() => import("./pages/Warranty"));
const CustomerMaster = lazy(() => import("./pages/CustomerMaster"));
const CreditDebitNotes = lazy(() => import("./pages/CreditDebitNotes"));
const RecurringInvoices = lazy(() => import("./pages/RecurringInvoices"));
const WebhookSettings = lazy(() => import("./pages/WebhookSettings"));
const AuditLog = lazy(() => import("./pages/AuditLog"));
const MetaIntegration = lazy(() => import("./pages/MetaIntegration"));
const VendorProfiles = lazy(() => import("./pages/VendorProfiles"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const SetPassword = lazy(() => import("./pages/SetPassword"));

const adminPages = {
  dashboard: {
    path: adminPath("/dashboard"),
    label: "Dashboard",
    eyebrow: "Overview",
    title: "Repair operations at a glance",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  leads: {
    path: adminPath("/leads"),
    label: "Leads",
    eyebrow: "CRM",
    title: "Lead management and conversions",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  repairs: {
    path: adminPath("/repairs"),
    label: "Repairs",
    eyebrow: "Tracking",
    title: "Repair tracking and customer updates",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.USER],
  },
  services: {
    path: adminPath("/services"),
    label: "Services",
    eyebrow: "Catalog",
    title: "Website service cards and pricing entries",
    allowedRoles: [ROLES.ADMIN],
  },
  products: {
    path: adminPath("/products"),
    label: "Products",
    eyebrow: "Refurbished",
    title: "Refurbished product catalog and pricing",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  inventory: {
    path: adminPath("/inventory"),
    label: "Inventory",
    eyebrow: "Stock",
    title: "Serial number inventory and stock units",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  orders: {
    path: adminPath("/orders"),
    label: "Orders",
    eyebrow: "Checkout",
    title: "Refurbished product orders",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  whatsapp: {
    path: adminPath("/whatsapp"),
    label: "WhatsApp",
    eyebrow: "CRM",
    title: "WhatsApp conversations and templates",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  whatsappTemplates: {
    path: adminPath("/whatsapp-templates"),
    label: "WhatsApp Templates",
    eyebrow: "Settings",
    title: "WhatsApp template management and sync",
    allowedRoles: [ROLES.ADMIN],
  },
  emailSettings: {
    path: adminPath("/email-settings"),
    label: "Email Settings",
    eyebrow: "Settings",
    title: "Email lead settings",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  deviceMasters: {
    path: adminPath("/device-masters"),
    label: "Device Masters",
    eyebrow: "Settings",
    title: "Repair device selection masters",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  paymentAccounts: {
    path: adminPath("/payment-accounts"),
    label: "Payment Accounts",
    eyebrow: "Settings",
    title: "Bank and UPI payment account settings",
    allowedRoles: [ROLES.ADMIN],
  },
  webhooks: {
    path: adminPath("/webhooks"),
    label: "Webhooks",
    eyebrow: "Settings",
    title: "Webhook addresses and signing secrets",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  auditLog: {
    path: adminPath("/audit-log"),
    label: "Audit Log",
    eyebrow: "Settings",
    title: "Read-only record of logged events",
    allowedRoles: [ROLES.ADMIN],
  },
  metaIntegration: {
    path: adminPath("/meta-integration"),
    label: "Meta Integration",
    eyebrow: "Settings",
    title: "WhatsApp Cloud API connection per vendor",
    allowedRoles: [ROLES.ADMIN],
  },
  vendorProfiles: {
    path: adminPath("/vendor-profiles"),
    label: "Vendors",
    eyebrow: "Platform",
    title: "Firm details for each vendor",
    scope: "platform",
    allowedRoles: [ROLES.SUPERADMIN],
  },
  vendorUsers: {
    path: adminPath("/vendor-users"),
    label: "Vendor Users",
    eyebrow: "Platform",
    title: "Admins and staff of each vendor",
    scope: "platform",
    allowedRoles: [ROLES.SUPERADMIN],
  },
  hero: {
    path: adminPath("/hero"),
    label: "Hero",
    eyebrow: "Homepage",
    title: "Enterprise homepage hero slides",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  popups: {
    path: adminPath("/popups"),
    label: "Popups",
    eyebrow: "Homepage",
    title: "Offers and announcements popup",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  reviews: {
    path: adminPath("/reviews"),
    label: "Reviews",
    eyebrow: "Automation",
    title: "Google review automation and coupons",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  billing: {
    path: adminPath("/billing"),
    label: "Billing",
    eyebrow: "ERP",
    title: "Proforma and tax invoice billing",
    topbarEyebrow: "ERP Billing",
    topbarTitle: "Invoice & Billing",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  estimates: {
    path: adminPath("/estimates"),
    label: "Estimates",
    eyebrow: "ERP",
    title: "Customer quotations and sale conversion",
    topbarEyebrow: "ERP Estimates",
    topbarTitle: "Estimates",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  payments: {
    path: adminPath("/payments"),
    label: "Payments",
    eyebrow: "ERP",
    title: "Receivables, settlements and payment audit",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  purchases: {
    path: adminPath("/purchases"),
    label: "Purchases",
    eyebrow: "ERP",
    title: "Supplier invoices, stock bought and money owed to suppliers",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  profitLoss: {
    path: adminPath("/profit-loss"),
    label: "Profit & Loss",
    eyebrow: "ERP",
    title: "Revenue, cost of goods sold, expenses and profit",
    module: "reports",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  creditNotes: {
    path: adminPath("/credit-notes"),
    label: "Credit / Debit Notes",
    eyebrow: "ERP",
    title: "Credit and debit notes against invoices",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  recurringInvoices: {
    path: adminPath("/recurring-invoices"),
    label: "Recurring Invoices",
    eyebrow: "ERP",
    title: "Billing schedules, generated on demand",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  reports: {
    path: adminPath("/reports"),
    label: "Reports",
    eyebrow: "Analytics",
    title: "Revenue, payments and business performance",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  warranty: {
    path: adminPath("/warranty"),
    label: "Warranty",
    eyebrow: "Service",
    title: "Active and expiring customer warranties",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  users: {
    path: adminPath("/users"),
    label: "Users",
    eyebrow: "Access",
    title: "Team hierarchy and customer access",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  customers: {
    path: adminPath("/customers"),
    label: "Customers",
    eyebrow: "Customer",
    title: "Customer master records",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
  },
  changePassword: {
    path: adminPath("/change-password"),
    label: "Change password",
    eyebrow: "Account",
    title: "Change your password",
    scope: "any",
    allowedRoles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER],
    showInNav: false,
  },
  customerProfile: {
    path: adminPath("/customers/:id/profile"),
    label: "Customer Profile",
    eyebrow: "Customer",
    title: "Customer workflow profile",
    module: "customers",
    allowedRoles: [ROLES.ADMIN, ROLES.MANAGER],
    showInNav: false,
  },
};

// The module a page belongs to (for a manager's or user's permissions[]): the page key itself unless the page says otherwise.
const moduleOfPage = (key, page) => {
  if (page.scope === "platform" || page.scope === "any") return undefined;
  return page.module !== undefined ? page.module : key;
};

function getPageKeyFromPath(pathname) {
  const routePath = stripAdminBase(pathname);
  if (routePath === "/") return "dashboard";
  if (/^\/customers\/[^/]+\/profile$/.test(routePath)) return "customerProfile";
  const match = Object.entries(adminPages).find(([, page]) => stripAdminBase(page.path) === routePath);
  return match?.[0] || null;
}

function getCustomerProfileId(pathname) {
  const routePath = stripAdminBase(pathname);
  const match = routePath.match(/^\/customers\/([^/]+)\/profile$/);
  return match ? decodeURIComponent(match[1]) : "";
}

export default function App() {
  const { booting, isAuthenticated, login: loginAdmin, hasRole, user } = useAuth();
  const pageAllowed = (key) => {
    const page = adminPages[key];
    return Boolean(page) && canOpenPage({ user, hasRole, allowedRoles: page.allowedRoles, scope: page.scope, module: moduleOfPage(key, page) });
  };
  // where an account lands: the platform account on its vendors, a vendor's staff on the first page they may open (the dashboard when they may)
  const defaultPageKey = user?.scope === "platform"
    ? "vendorProfiles"
    : (pageAllowed("dashboard") ? "dashboard" : Object.keys(adminPages).find((key) => adminPages[key].showInNav !== false && pageAllowed(key)) || "dashboard");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState("");
  const [pageKey, setPageKey] = useState(() => getPageKeyFromPath(window.location.pathname) || "dashboard");
  const pageMeta = adminPages[pageKey] || adminPages.dashboard;

  useEffect(() => {
    const syncPage = () => {
      const nextPageKey = getPageKeyFromPath(window.location.pathname);
      if (!nextPageKey) {
        window.history.replaceState({}, "", adminPages.dashboard.path);
        setPageKey("dashboard");
        return;
      }
      setPageKey(nextPageKey);
    };
    syncPage();
    window.addEventListener("popstate", syncPage);
    return () => window.removeEventListener("popstate", syncPage);
  }, []);

  useEffect(() => watchCardTables(), []);

  useEffect(() => {
    document.title = isAuthenticated ? `${pageMeta.label} | Yansh Admin` : "Admin Login | Yansh Admin";
  }, [isAuthenticated, pageMeta.label]);

  useEffect(() => {
    if (isAuthenticated && !isAdminEntryPath(window.location.pathname) && stripAdminBase(window.location.pathname) === "/") {
      window.history.replaceState({}, "", adminPages[defaultPageKey].path);
      setPageKey(defaultPageKey);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // An address typed or bookmarked for a page this account may not open (the platform account on a vendor page, or the other way round) moves
  // to the page it may open instead of showing someone else's workspace.
  useEffect(() => {
    if (booting || !isAuthenticated || !user || pageAllowed(pageKey) || !pageAllowed(defaultPageKey) || pageKey === defaultPageKey) return;
    window.history.replaceState({}, "", adminPages[defaultPageKey].path);
    setPageKey(defaultPageKey);
  }, [booting, isAuthenticated, user, pageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loginLoading) return;
    setError("");
    setLoginLoading(true);
    try {
      await loginAdmin(login);
      setLogin({ username: "", password: "" });
      setPageKey(getPageKeyFromPath(window.location.pathname));
    } catch (err) {
      setError(err.response?.data?.message || "Login failed.");
    } finally {
      setLoginLoading(false);
    }
  };

  // `query` carries a destination filter (for example a dashboard card's ?status=ready); the page reads it from the URL when it opens.
  const navigate = (nextKey, query) => {
    const nextPage = adminPages[nextKey] || adminPages.dashboard;
    window.history.pushState({}, "", withQuery(nextPage.path, query));
    setPageKey(nextKey);
  };

  const navigatePath = (path) => {
    const nextPath = adminPath(path);
    window.history.pushState({}, "", nextPath);
    const nextPageKey = getPageKeyFromPath(new URL(nextPath, window.location.origin).pathname);
    if (nextPageKey) {
      setPageKey(nextPageKey);
    }
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const renderPage = () => {
    const page = adminPages[pageKey] || adminPages.dashboard;
    const content = (() => {
      switch (pageKey) {
        case "leads":
          return <LeadsPage onNavigate={navigate} currentPage={pageKey} />;
        case "repairs":
          return <RepairsModern onNavigate={navigate} currentPage={pageKey} />;
        case "services":
          return <AddService onNavigate={navigate} currentPage={pageKey} />;
        case "products":
          return <Products onNavigate={navigate} currentPage={pageKey} />;
        case "inventory":
          return <Inventory onNavigate={navigate} currentPage={pageKey} />;
        case "orders":
          return <Orders onNavigate={navigate} currentPage={pageKey} />;
        case "whatsapp":
          return <WhatsApp onNavigatePath={navigatePath} />;
        case "whatsappTemplates":
          return <WhatsAppTemplateManagement />;
        case "emailSettings":
          return <ManagedSettingsGate family="email"><EmailSettings /></ManagedSettingsGate>;
        case "deviceMasters":
          return <DeviceMasters />;
        case "paymentAccounts":
          return <PaymentAccountSettings />;
        case "webhooks":
          return <ManagedSettingsGate family="webhooks"><WebhookSettings /></ManagedSettingsGate>;
        case "auditLog":
          return <AuditLog />;
        case "metaIntegration":
          return <ManagedSettingsGate family="whatsapp"><MetaIntegration /></ManagedSettingsGate>;
        case "vendorProfiles":
          return <VendorProfiles />;
        case "vendorUsers":
          return <Users onNavigate={navigate} currentPage={pageKey} />;
        case "changePassword":
          return <ChangePassword />;
        case "hero":
          return <HeroSlides onNavigate={navigate} currentPage={pageKey} />;
        case "popups":
          return <Popups onNavigate={navigate} currentPage={pageKey} />;
        case "reviews":
          return <Reviews onNavigate={navigate} currentPage={pageKey} />;
        case "billing":
          return <Billing onNavigate={navigate} currentPage={pageKey} />;
        case "estimates":
          return <Estimates onNavigate={navigate} currentPage={pageKey} />;
        case "payments":
          return <Payments onNavigate={navigate} currentPage={pageKey} />;
        case "purchases":
          return <Purchases />;
        case "profitLoss":
          return <ProfitLoss onNavigate={navigate} />;
        case "creditNotes":
          return <CreditDebitNotes />;
        case "recurringInvoices":
          return <RecurringInvoices />;
        case "reports":
          return <AnalyticsDashboard />;
        case "warranty":
          return <Warranty onNavigatePath={navigatePath} />;
        case "users":
          return <Users onNavigate={navigate} currentPage={pageKey} />;
        case "customers":
          return <CustomerMaster onNavigatePath={navigatePath} />;
        case "customerProfile":
          return <CustomerProfile customerId={getCustomerProfileId(window.location.pathname)} />;
        case "dashboard":
        default:
          return <DashboardModern onNavigate={navigate} currentPage={pageKey} />;
      }
    })();

    return (
      <ProtectedRoute allowedRoles={page.allowedRoles} scope={page.scope} module={moduleOfPage(pageKey, page)}>
        {content}
      </ProtectedRoute>
    );
  };

  const visiblePages = Object.entries(adminPages).filter(([key, page]) => (
    page.showInNav !== false &&
    pageAllowed(key)
  ));
  const navigationItems = visiblePages.map(([id, page]) => ({
    id,
    label: page.label,
  }));

  // the one-time link a user opens to set a password: it works before, and without, a sign-in
  if (stripAdminBase(window.location.pathname) === "/set-password") {
    return <Suspense fallback={null}><SetPassword /></Suspense>;
  }

  if (booting) {
    return (
      <main className="login-shell">
        <section className="login-panel">
          <p className="login-error">Loading admin session...</p>
        </section>
      </main>
    );
  }

  // a user who was given a password by someone else sees nothing but this until they choose their own
  if (isAuthenticated && user?.mustChangePassword) {
    return <Suspense fallback={null}><ChangePassword forced /></Suspense>;
  }

  if (!isAuthenticated) {
    return (
      <main className="login-shell">
        <SEO title="Login" description="Yansh Technologies admin login" />
        <section className="login-panel">
          <div className="login-brand">
            <img src={yanshLogo} alt="Yansh Technologies" />
            <div>
              <p>Admin sign-in</p>
              <h1>Yansh Technologies</h1>
            </div>
          </div>

          <form className="login-form" onSubmit={handleLogin}>
            <label>
              Username
              <input
                value={login.username}
                onChange={(event) => setLogin({ ...login, username: event.target.value })}
                placeholder="Enter username"
                autoComplete="username"
                disabled={loginLoading}
              />
            </label>

            <label>
              Password
              <input
                value={login.password}
                onChange={(event) => setLogin({ ...login, password: event.target.value })}
                placeholder="Enter password"
                type="password"
                autoComplete="current-password"
                disabled={loginLoading}
              />
            </label>

            <button type="submit" disabled={loginLoading || !login.username.trim() || !login.password}>
              {loginLoading ? "Logging in..." : "Login to Panel"}
            </button>
            {error && <p className="login-error">{error}</p>}
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-shell dashboard-modern">
      <SEO title={pageMeta.label} description={pageMeta.title} />
      <CRMSidebar
        currentPage={pageKey}
        items={navigationItems}
        onNavigate={navigate}
      />
      <div className="dashboard-main">
        <TopBar
          pageContext={{
            eyebrow: pageMeta.topbarEyebrow || pageMeta.eyebrow,
            title: pageMeta.topbarTitle || pageMeta.label,
          }}
          onNavigate={navigate}
        />
        <section className={`workspace dashboard-content${pageKey === "whatsapp" ? " whatsapp-dashboard-content" : ""}`}>
          <Suspense fallback={<section className="panel"><p className="empty-state">Loading workspace...</p></section>}>
            {renderPage()}
          </Suspense>
        </section>
      </div>
    </main>
  );
}
